import ImageUploader from 'components/ImageUploader';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import PulseLoader from 'react-spinners/PulseLoader';
import Skeleton from 'react-loading-skeleton';

// MaterialUI
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';

// app: components
import DisplayNameChooser from 'components/DisplayNameChooser';
import RichTextField from 'components/RichTextField';

// app: constants
import { COMMENT_RULES } from 'constants/index';

// assets
import CssCommentForm from './CommentForm.scss';

const ResponseForm = React.forwardRef((props, ref) => {
  const {
    focused,
    id,
    isOpen,
    loading,
    onBlur,
    onChange,
    isEditing,
    onClose,
    onClosed,
    onFocus,
    onSubmit,
    optionsMenu,
    maxLength,
    respondAsCkbk,
    response,
    saveButtonText,
    saveError,
    saveInProgress,
    showUserInfo,
    isModerator,
    textFieldLabel,
    type,
    imageUrls,
    uploadingError,
    setUploadingError,
    isUploadingImage,
    setReviewImage,
    onRemoveExistingImage,
  } = props;

  useStyles(CssCommentForm);

  const [isDirty, setDirty] = useState(false);
  const [reviewLength, setReviewLength] = useState(0);

  const handleResponseChange = htmlContent => {
    setDirty(true);
    onChange({ response: htmlContent });
  };

  const handleSubmit = event => {
    event.preventDefault();
    onSubmit({ response });
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => onChange({ response: '' }), 300);
  };

  const handleBlur = event => {
    setDirty(!!reviewLength);
    onBlur(event);
  };

  const handleImageSelected = file => {
    setUploadingError(null);

    setReviewImage(file);
  };

  const handleImageValidationError = error => {
    console.error('validate image error response form = ', error);
    setUploadingError(null);
  };

  return (
    <Collapse in={isOpen} timeout={300} onExited={onClosed}>
      <form noValidate className="comment-form comment-form--response" onSubmit={handleSubmit}>
        {showUserInfo ? (
          <div className="comment-form__row">
            {respondAsCkbk ? (
              <p>
                Responding as: <strong>ckbk</strong>
              </p>
            ) : (
              <DisplayNameChooser showAvatar={false} label="Responding as" />
            )}
          </div>
        ) : null}
        <div className={`comment-form__row ${!isEditing ? 'margin-top-10' : ''}`}>
          {loading ? (
            <div style={{ width: '100%' }}>
              <Skeleton width="100%" height="71px" />
            </div>
          ) : (
            <RichTextField
              ref={ref}
              fullWidth
              focused={focused}
              isDirty={isDirty}
              maxLength={maxLength}
              minLength={1}
              id={id}
              placeholder={textFieldLabel}
              handleReviewChange={handleResponseChange}
              review={response}
              setReviewLength={setReviewLength}
              isOpen={isOpen}
              isModerator={isModerator}
              handleBlur={handleBlur}
              onFocus={onFocus}
            />
          )}
        </div>
        {optionsMenu ? <div className="comment-form__dots-menu">{optionsMenu}</div> : null}
        {type === 'review' && !showUserInfo ? (
          <ImageUploader
            className="comment-form__img-uploader"
            isOpen={isOpen}
            isLoading={loading}
            existingImage={imageUrls && imageUrls.length ? imageUrls[0] : null}
            uploadError={uploadingError}
            isUploading={isUploadingImage}
            onImageSelected={handleImageSelected}
            onRemoveExistingImage={onRemoveExistingImage}
            onValidationError={handleImageValidationError}
          />
        ) : null}
        {saveError ? (
          <div className="comment-form__row comment-form__centered margin-top-10">
            <Alert severity="error">{saveError}</Alert>
          </div>
        ) : null}

        <div className="comment-form__row comment-form__buttons margin-top-10 margin-bottom-10">
          <Button
            className="comment-form__button"
            onClick={handleClose}
            size="large"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            className="comment-form__button"
            color="primary"
            disabled={!reviewLength || saveInProgress || loading}
            size="large"
            type="submit"
            variant="contained"
          >
            <PulseLoader loading={saveInProgress} color="#fff" sizeUnit="px" size={10} />
            {saveInProgress ? '' : saveButtonText}
          </Button>
        </div>
      </form>
    </Collapse>
  );
});

ResponseForm.propTypes = {
  focused: PropTypes.bool,
  isModerator: PropTypes.bool,
  response: PropTypes.string,
  id: PropTypes.string,
  isOpen: PropTypes.bool,
  type: PropTypes.string,
  imageUrls: PropTypes.arrayOf(PropTypes.string),
  isEditing: PropTypes.bool,
  maxLength: PropTypes.number,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  onClosed: PropTypes.func,
  onFocus: PropTypes.func,
  onSubmit: PropTypes.func,
  optionsMenu: PropTypes.node,
  saveInProgress: PropTypes.bool,
  saveButtonText: PropTypes.string,
  saveError: PropTypes.string,
  showAsEditButton: PropTypes.bool,
  showUserInfo: PropTypes.bool,
  textFieldLabel: PropTypes.string,
  setUploadingError: PropTypes.func,
  setReviewImage: PropTypes.func,
  onRemoveExistingImage: PropTypes.func,
  uploadingError: PropTypes.string,
  isUploadingImage: PropTypes.bool,
};

ResponseForm.defaultProps = {
  focused: false,
  isModerator: false,
  isEditing: false,
  id: 'response',
  type: 'review',
  imageUrls: [],
  isOpen: false,
  onBlur: () => {},
  onChange: () => {},
  onClose: () => {},
  onClosed: () => {},
  onFocus: () => {},
  onSubmit: () => {},
  optionsMenu: null,
  maxLength: COMMENT_RULES.response.maxLength,
  response: '',
  saveButtonText: 'Post reply',
  saveError: null,
  saveInProgress: false,
  showAsEditButton: false,
  showUserInfo: true,
  textFieldLabel: 'Your response',
  setUploadingError: () => {},
  onRemoveExistingImage: () => {},
  setReviewImage: () => {},
  uploadingError: null,
  isUploadingImage: false,
};

export default React.memo(ResponseForm);
